import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import PandaWatermark from '@/assets/icons/cover-images/panda-watermark.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var29');

            const holderInner = document.createElement('div');
            holderInner.classList.add('holder-inner-var29');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('image-var29');

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('image-var29');

            const leftDiv = document.createElement('div');
            leftDiv.classList.add('left-div-var29');

            const rightDiv = document.createElement('div');
            rightDiv.classList.add('right-div-var29');

            leftDiv.appendChild(leftImage);

            const leftWatermark = createWatermarkTagImage(
              'left-actual-photo-var29',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftWatermark) leftDiv.appendChild(leftWatermark);

            rightDiv.appendChild(rightImage);

            const rightWatermark = createWatermarkTagImage(
              'right-actual-photo-var29',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightWatermark) rightDiv.appendChild(rightWatermark);

            if (variation.topText) {
              const topText = document.createElement('div');
              topText.classList.add('top-text-var29');
              let text = variation.topText;
              if (variation.topTextBrakes) {
                topText.innerText = text;
              } else {
                topText.innerHTML = text;
              }
              holderInner.appendChild(topText);
            }

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break-var29');

              if (variation.divider === 'black') {
                middleBreak.classList.add('break-var29--black-var29');
              }

              imageHolder.appendChild(middleBreak);
            }

            if (variation.leftText) {
              const leftText = document.createElement('div');
              leftText.classList.add('left-text-var29');

              if (variation.divider !== 'none') {
                leftText.classList.add('left-text__divider-var29');
              }

              const leftP = document.createElement('p');
              const updatedText = replaceQuotes(variation.leftText);
              let text = '';
              if (variation.leftQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.leftBreakes) {
                leftP.innerText = text;
                leftP.classList.add('custom-brakes-var29');
              } else {
                leftP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 476,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              leftText.appendChild(leftP);

              if (variation.leftTextPositon === 'top') {
                leftText.classList.add('left-text-var29--top-var29');
              }

              leftDiv.appendChild(leftText);
            }

            if (variation.rightText) {
              const rightText = document.createElement('div');
              rightText.classList.add('right-text-var29');

              if (variation.divider !== 'none') {
                rightText.classList.add('right-text__divider-var29');
              }

              const rightP = document.createElement('p');
              const updatedText = replaceQuotes(variation.rightText);
              let text = '';
              if (variation.rightQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.rightBreakes) {
                rightP.innerText = text;
                rightP.classList.add('custom-brakes-var29');
              } else {
                rightP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 476,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              rightText.appendChild(rightP);

              if (variation.rightTextPositon === 'top') {
                rightText.classList.add('right-text-var29--top-var29');
              }

              rightDiv.appendChild(rightText);
            }

            imageHolder.appendChild(leftDiv);
            imageHolder.appendChild(rightDiv);
            if (variation.watermark) {
              const pandaWatermark = new Image();
              pandaWatermark.classList.add('watermark-var29');
              pandaWatermark.src = PandaWatermark;
              imageHolder.appendChild(pandaWatermark);
            }
            holderInner.appendChild(imageHolder);

            return holderInner;
          },
          {
            holderClassName: 'generated-images-holder-var29',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
